// frontend/src/pages/catalog/ProductsPage.tsx
//
// 商品管理页：商品列表 + 新建/编辑商品 Modal + 抽屉管理商品价格。
// 后端：/merchant/catalog/products、/merchant/catalog/prices。
// 金额统一以元输入（×100 提交），展示统一 分→元 两位小数 + 币种。

import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  DollarOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import CatalogService, {
  ListMeta,
  Price,
  PriceType,
  Product,
  RecurringInterval,
} from '../../services/catalog';
import { formatAmount, yuanToCents } from '../../utils/money';

const { Title, Text } = Typography;

// 商品类型
export const PRODUCT_TYPE_OPTIONS = [
  { value: 'digital', label: '数字商品' },
  { value: 'service', label: '服务' },
  { value: 'good', label: '实物商品' },
];

const PRODUCT_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  digital: { label: '数字商品', color: 'blue' },
  service: { label: '服务', color: 'purple' },
  good: { label: '实物商品', color: 'green' },
};

// 税务品类
export const TAX_CATEGORY_OPTIONS = [
  { value: 'digital_goods', label: '数字商品 (digital_goods)' },
  { value: 'saas', label: 'SaaS 订阅 (saas)' },
  { value: 'ebook', label: '电子书 (ebook)' },
  { value: 'service', label: '服务 (service)' },
  { value: 'physical', label: '实体商品 (physical)' },
];

const TAX_CATEGORY_LABELS: Record<string, string> = {
  digital_goods: '数字商品',
  saas: 'SaaS 订阅',
  ebook: '电子书',
  service: '服务',
  physical: '实体商品',
};

// 币种
const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'CNY', 'JPY', 'MYR', 'SGD', 'HKD'].map((c) => ({
  value: c,
  label: c,
}));

const INTERVAL_OPTIONS: { value: RecurringInterval; label: string }[] = [
  { value: 'day', label: '天' },
  { value: 'week', label: '周' },
  { value: 'month', label: '月' },
  { value: 'year', label: '年' },
];

export const INTERVAL_LABELS: Record<string, string> = {
  day: '天',
  week: '周',
  month: '月',
  year: '年',
};

/** 价格周期文案：每 N 月 / 每月 */
export const recurringText = (price: Price): string => {
  if (price.type !== 'recurring') return '单次';
  const count = price.recurring_interval_count || 1;
  const unit = INTERVAL_LABELS[price.recurring_interval || 'month'] || price.recurring_interval;
  return count > 1 ? `每 ${count} ${unit}` : `每${unit}`;
};

const ProductsPage: React.FC = () => {
  // 商品列表
  const [products, setProducts] = useState<Product[]>([]);
  const [meta, setMeta] = useState<ListMeta>({
    total: 0,
    per_page: 20,
    current_page: 1,
    last_page: 1,
  });
  const [loading, setLoading] = useState(false);
  // 商品价格数（product_id → count）
  const [priceCounts, setPriceCounts] = useState<Record<string, number>>({});

  // 商品 Modal
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [savingProduct, setSavingProduct] = useState(false);
  const [productForm] = Form.useForm();

  // 价格抽屉
  const [priceDrawerOpen, setPriceDrawerOpen] = useState(false);
  const [priceProduct, setPriceProduct] = useState<Product | null>(null);
  const [prices, setPrices] = useState<Price[]>([]);
  const [pricesLoading, setPricesLoading] = useState(false);

  // 价格 Modal
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [savingPrice, setSavingPrice] = useState(false);
  const [priceForm] = Form.useForm();
  const priceTypeValue: PriceType = Form.useWatch('type', priceForm) || 'one_time';

  const loadProducts = useCallback(async (page: number = 1, perPage: number = 20) => {
    setLoading(true);
    try {
      const result = await CatalogService.listProducts(page, perPage);
      setProducts(result.data);
      setMeta(result.meta);
      // 拉取价格做"价格数"统计（取前 200 条足够展示用途）
      try {
        const priceResult = await CatalogService.listPrices(undefined, 1, 200);
        const counts: Record<string, number> = {};
        priceResult.data.forEach((p) => {
          counts[p.product_id] = (counts[p.product_id] || 0) + 1;
        });
        setPriceCounts(counts);
      } catch {
        // 统计失败不影响主列表
      }
    } catch (err: any) {
      message.error(err.response?.data?.message || '获取商品列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // ---- 商品新建 / 编辑 ----

  const openCreateProduct = () => {
    setEditingProduct(null);
    productForm.resetFields();
    setProductModalOpen(true);
  };

  const openEditProduct = (product: Product) => {
    setEditingProduct(product);
    productForm.setFieldsValue({
      name: product.name,
      description: product.description,
      type: product.type,
      tax_category: product.tax_category,
    });
    setProductModalOpen(true);
  };

  const handleSaveProduct = async () => {
    try {
      const values = await productForm.validateFields();
      setSavingProduct(true);
      if (editingProduct) {
        await CatalogService.updateProduct(editingProduct.id, values);
        message.success('商品已更新');
      } else {
        await CatalogService.createProduct({ ...values, active: true });
        message.success('商品已创建');
      }
      setProductModalOpen(false);
      productForm.resetFields();
      loadProducts(meta.current_page, meta.per_page);
    } catch (err: any) {
      if (err.errorFields) return; // 表单校验错误
      message.error(err.response?.data?.message || '保存商品失败');
    } finally {
      setSavingProduct(false);
    }
  };

  const handleDeactivateProduct = async (product: Product) => {
    try {
      await CatalogService.deactivateProduct(product.id);
      message.success('商品已停用');
      loadProducts(meta.current_page, meta.per_page);
    } catch (err: any) {
      message.error(err.response?.data?.message || '停用商品失败');
    }
  };

  // ---- 价格管理 ----

  const loadPrices = useCallback(async (productId: string) => {
    setPricesLoading(true);
    try {
      const result = await CatalogService.listPrices(productId, 1, 100);
      setPrices(result.data);
    } catch (err: any) {
      message.error(err.response?.data?.message || '获取价格列表失败');
    } finally {
      setPricesLoading(false);
    }
  }, []);

  const openPriceDrawer = (product: Product) => {
    setPriceProduct(product);
    setPriceDrawerOpen(true);
    loadPrices(product.id);
  };

  const openCreatePrice = () => {
    priceForm.resetFields();
    priceForm.setFieldsValue({
      currency: 'USD',
      type: 'one_time',
      recurring_interval: 'month',
      recurring_interval_count: 1,
      trial_period_days: 0,
    });
    setPriceModalOpen(true);
  };

  const handleSavePrice = async () => {
    if (!priceProduct) return;
    try {
      const values = await priceForm.validateFields();
      setSavingPrice(true);
      // 规格属性：[{key,value}] → {key: value}，过滤空键
      const attributes: Record<string, string> = {};
      (values.attributes || []).forEach((row: { key?: string; value?: string }) => {
        const k = (row?.key || '').trim();
        if (k) attributes[k] = (row?.value || '').trim();
      });
      const payload: Partial<Price> = {
        product_id: priceProduct.id,
        currency: values.currency,
        unit_amount: yuanToCents(values.unit_amount), // 以元输入 ×100 提交
        type: values.type,
        nickname: values.nickname,
        sku: (values.sku || '').trim() || undefined,
        variant_name: (values.variant_name || '').trim() || undefined,
        attributes: Object.keys(attributes).length ? attributes : undefined,
        active: true,
      };
      if (values.type === 'recurring') {
        payload.recurring_interval = values.recurring_interval;
        payload.recurring_interval_count = values.recurring_interval_count || 1;
        payload.trial_period_days = values.trial_period_days || 0;
      }
      await CatalogService.createPrice(payload);
      message.success('价格已创建');
      setPriceModalOpen(false);
      priceForm.resetFields();
      loadPrices(priceProduct.id);
      // 更新价格数
      setPriceCounts((prev) => ({
        ...prev,
        [priceProduct.id]: (prev[priceProduct.id] || 0) + 1,
      }));
    } catch (err: any) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || '创建价格失败');
    } finally {
      setSavingPrice(false);
    }
  };

  const handleDeactivatePrice = async (price: Price) => {
    try {
      await CatalogService.deactivatePrice(price.id);
      message.success('价格已停用');
      if (priceProduct) loadPrices(priceProduct.id);
    } catch (err: any) {
      message.error(err.response?.data?.message || '停用价格失败');
    }
  };

  // ---- 表格列 ----

  const productColumns: ColumnsType<Product> = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record) => (
        <div>
          <Text strong>{name}</Text>
          {record.description && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.description}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 110,
      render: (type: string) => {
        const conf = PRODUCT_TYPE_LABELS[type] || { label: type, color: 'default' };
        return <Tag color={conf.color}>{conf.label}</Tag>;
      },
    },
    {
      title: '税务品类',
      dataIndex: 'tax_category',
      key: 'tax_category',
      width: 120,
      render: (cat: string) => (cat ? TAX_CATEGORY_LABELS[cat] || cat : <Text type="secondary">-</Text>),
    },
    {
      title: '价格数',
      key: 'price_count',
      width: 90,
      render: (_, record) => priceCounts[record.id] || 0,
    },
    {
      title: '状态',
      dataIndex: 'active',
      key: 'active',
      width: 90,
      render: (active: boolean) =>
        active ? <Tag color="success">启用</Tag> : <Tag color="default">已停用</Tag>,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (t: string) => (t ? dayjs(t).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 230,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditProduct(record)}>
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            icon={<DollarOutlined />}
            onClick={() => openPriceDrawer(record)}
          >
            价格
          </Button>
          {record.active && (
            <Popconfirm
              title="确认停用该商品？"
              okText="确认"
              cancelText="取消"
              onConfirm={() => handleDeactivateProduct(record)}
            >
              <Button type="link" size="small" danger icon={<StopOutlined />}>
                停用
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const priceColumns: ColumnsType<Price> = [
    {
      title: '名称',
      dataIndex: 'nickname',
      key: 'nickname',
      render: (nickname: string, record) => nickname || <Text type="secondary">{record.id}</Text>,
    },
    {
      title: '规格',
      dataIndex: 'variant_name',
      key: 'variant_name',
      width: 120,
      render: (variant: string) =>
        variant ? <Tag color="cyan">{variant}</Tag> : <Text type="secondary">-</Text>,
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      width: 140,
      render: (sku: string) =>
        sku ? <Text code>{sku}</Text> : <Text type="secondary">-</Text>,
    },
    {
      title: '金额',
      dataIndex: 'unit_amount',
      key: 'unit_amount',
      render: (amount: number, record) => <Text strong>{formatAmount(amount, record.currency)}</Text>,
    },
    {
      title: '计费',
      key: 'billing',
      render: (_, record) =>
        record.type === 'recurring' ? (
          <Tag color="blue">{recurringText(record)}</Tag>
        ) : (
          <Tag>单次</Tag>
        ),
    },
    {
      title: '试用',
      dataIndex: 'trial_period_days',
      key: 'trial_period_days',
      width: 80,
      render: (days: number) => (days ? `${days} 天` : '-'),
    },
    {
      title: '状态',
      dataIndex: 'active',
      key: 'active',
      width: 90,
      render: (active: boolean) =>
        active ? <Tag color="success">启用</Tag> : <Tag color="default">已停用</Tag>,
    },
    {
      title: '操作',
      key: 'actions',
      width: 90,
      render: (_, record) =>
        record.active ? (
          <Popconfirm
            title="确认停用该价格？"
            okText="确认"
            cancelText="取消"
            onConfirm={() => handleDeactivatePrice(record)}
          >
            <Button type="link" size="small" danger>
              停用
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <div>
      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            商品管理
          </Title>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadProducts(meta.current_page, meta.per_page)}
            >
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateProduct}>
              新建商品
            </Button>
          </Space>
        </div>

        <Table<Product>
          rowKey="id"
          columns={productColumns}
          dataSource={products || []}
          loading={loading}
          locale={{ emptyText: '暂无数据' }}
          pagination={{
            current: meta.current_page,
            pageSize: meta.per_page,
            total: meta.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => loadProducts(page, pageSize),
          }}
        />
      </Card>

      {/* 新建 / 编辑商品 */}
      <Modal
        title={editingProduct ? '编辑商品' : '新建商品'}
        open={productModalOpen}
        onOk={handleSaveProduct}
        onCancel={() => setProductModalOpen(false)}
        confirmLoading={savingProduct}
        okText={editingProduct ? '保存' : '创建'}
        cancelText="取消"
        destroyOnClose
      >
        <Form form={productForm} layout="vertical" initialValues={{ type: 'digital' }}>
          <Form.Item
            label="商品名称"
            name="name"
            rules={[{ required: true, message: '请输入商品名称' }]}
          >
            <Input placeholder="例如：高级会员（年付）" maxLength={255} />
          </Form.Item>
          <Form.Item label="描述" name="description">
            <Input.TextArea rows={3} placeholder="商品描述（展示在收银台）" maxLength={1000} />
          </Form.Item>
          <Form.Item
            label="商品类型"
            name="type"
            rules={[{ required: true, message: '请选择商品类型' }]}
          >
            <Select options={PRODUCT_TYPE_OPTIONS} placeholder="请选择商品类型" />
          </Form.Item>
          <Form.Item label="税务品类" name="tax_category">
            <Select allowClear options={TAX_CATEGORY_OPTIONS} placeholder="请选择税务品类（用于自动算税）" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 价格管理抽屉 */}
      <Drawer
        title={priceProduct ? `价格管理 · ${priceProduct.name}` : '价格管理'}
        width={720}
        open={priceDrawerOpen}
        onClose={() => setPriceDrawerOpen(false)}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreatePrice}>
            新建价格
          </Button>
        }
      >
        <Table<Price>
          rowKey="id"
          columns={priceColumns}
          dataSource={prices || []}
          loading={pricesLoading}
          locale={{ emptyText: '暂无数据' }}
          pagination={false}
          size="small"
        />
      </Drawer>

      {/* 新建价格 */}
      <Modal
        title={priceProduct ? `新建价格 · ${priceProduct.name}` : '新建价格'}
        open={priceModalOpen}
        onOk={handleSavePrice}
        onCancel={() => setPriceModalOpen(false)}
        confirmLoading={savingPrice}
        okText="创建"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={priceForm} layout="vertical">
          <Form.Item label="价格名称（可选）" name="nickname">
            <Input placeholder="例如：月付标准价" maxLength={255} />
          </Form.Item>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item label="规格名（可选）" name="variant_name" style={{ flex: 1 }}>
              <Input placeholder="例如：红色 / L" maxLength={255} />
            </Form.Item>
            <Form.Item
              label="SKU（可选，同商户唯一）"
              name="sku"
              style={{ flex: 1 }}
            >
              <Input placeholder="例如：TSHIRT-RED-L" maxLength={100} />
            </Form.Item>
          </div>
          <Form.Item label="规格属性（可选）">
            <Form.List name="attributes">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item name={[field.name, 'key']} noStyle>
                        <Input placeholder="属性名，如 color" style={{ width: 160 }} maxLength={50} />
                      </Form.Item>
                      <Form.Item name={[field.name, 'value']} noStyle>
                        <Input placeholder="属性值，如 红色" style={{ width: 200 }} maxLength={100} />
                      </Form.Item>
                      <Button type="link" danger size="small" onClick={() => remove(field.name)}>
                        删除
                      </Button>
                    </Space>
                  ))}
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    添加规格属性
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item
              label="币种"
              name="currency"
              style={{ width: 140 }}
              rules={[{ required: true, message: '请选择币种' }]}
            >
              <Select options={CURRENCY_OPTIONS} />
            </Form.Item>
            <Form.Item
              label="金额（元）"
              name="unit_amount"
              style={{ flex: 1 }}
              rules={[{ required: true, message: '请输入金额' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0.01}
                precision={2}
                placeholder="例如 9.99"
              />
            </Form.Item>
          </div>
          <Form.Item
            label="计费类型"
            name="type"
            rules={[{ required: true, message: '请选择计费类型' }]}
          >
            <Select
              options={[
                { value: 'one_time', label: '单次（一次性付款）' },
                { value: 'recurring', label: '订阅（周期性扣款）' },
              ]}
            />
          </Form.Item>
          {priceTypeValue === 'recurring' && (
            <>
              <div style={{ display: 'flex', gap: 12 }}>
                <Form.Item
                  label="计费周期"
                  name="recurring_interval"
                  style={{ flex: 1 }}
                  rules={[{ required: true, message: '请选择计费周期' }]}
                >
                  <Select options={INTERVAL_OPTIONS} />
                </Form.Item>
                <Form.Item
                  label="周期数"
                  name="recurring_interval_count"
                  style={{ flex: 1 }}
                  rules={[{ required: true, message: '请输入周期数' }]}
                >
                  <InputNumber style={{ width: '100%' }} min={1} precision={0} />
                </Form.Item>
              </div>
              <Form.Item label="试用天数" name="trial_period_days">
                <InputNumber style={{ width: '100%' }} min={0} precision={0} placeholder="0 表示无试用" />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default ProductsPage;
